import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MonitoringLayout from "../components/MonitoringLayout";
import apiFetch from "../utils/apiFetch";
import useDebounce from "../hooks/useDebounce";

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Asia/Tashkent",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

const STATUS_LABELS = {
  MASTER_REVIEW: "Мастер",
  LINE_CHIEF_REVIEW: "Тармоқ бошлиғи",
  BRANCH_ENGINEER_REVIEW: "Бош муҳандис",
  CLOSED: "Ёпилди",
  DISMISSED: "Рад этилди",
};

const STATUS_BADGE = {
  MASTER_REVIEW: "master",
  LINE_CHIEF_REVIEW: "chief",
  BRANCH_ENGINEER_REVIEW: "engineer",
  CLOSED: "closed",
  DISMISSED: "dismissed",
};

const TABS = [
  { value: "", label: "Барчаси" },
  { value: "MASTER_REVIEW", label: "Мастер" },
  { value: "LINE_CHIEF_REVIEW", label: "Тармоқ бошлиғи" },
  { value: "BRANCH_ENGINEER_REVIEW", label: "Бош муҳандис" },
  { value: "OVERDUE", label: "Муддати ўтган" },
  { value: "CLOSED", label: "Ёпилган" },
];

const LIMIT = 30;

export default function Monitoring() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(() => {
    if (searchParams.get("overdue") === "true") return "OVERDUE";
    return searchParams.get("status") || "";
  });
  const [emergencyOnly, setEmergencyOnly] = useState(() => searchParams.get("is_emergency") === "true");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const load = useCallback(
    async (p, s, q, em) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ page: p });
        if (s) params.set("status", s);
        if (q) params.set("search", q);
        if (em) params.set("is_emergency", "true");
        const res = await apiFetch(`/api/incidents?${params}`);
        if (!res.ok) throw new Error("Юкланмади");
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (e?.status === 401) navigate("/login");
        else if (e?.status === 403) navigate("/no-access");
        else setError(e.message || "Хатолик юз берди");
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    load(page, status, debouncedSearch, emergencyOnly);
  }, [page, status, debouncedSearch, emergencyOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(1, status, search, emergencyOnly);
  };

  const handleTab = (v) => {
    setStatus(v);
    setPage(1);
  };

  const toggleEmergency = () => {
    setEmergencyOnly((p) => !p);
    setPage(1);
  };

  const isOverdue = (dueAt, s) => {
    if (!dueAt) return false;
    if (s === "CLOSED" || s === "DISMISSED" || s === "FAILED") return false;
    return new Date(dueAt) < new Date();
  };

  return (
    <MonitoringLayout>
      <div className="mon-page-header">
        <div>
          <h1 className="mon-page-title">Нуқсон ва камчилик аниқланган таянчлар</h1>
        </div>
      </div>

      <div className="mon-filters">
        <div className="mon-status-tabs">
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              className={"mon-status-tab" + (status === t.value ? " is-active" : "")}
              onClick={() => handleTab(t.value)}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            className={"mon-status-tab mon-status-tab--emergency" + (emergencyOnly ? " is-active" : "")}
            onClick={toggleEmergency}
          >
            🚨 Авариявий
          </button>
        </div>
        <form className="mon-search" onSubmit={handleSearch}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Таяч номи бўйича қидириш..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>
      </div>

      {loading && <div className="mon-loading">Юкланмоқда...</div>}
      {error && !loading && <div className="mon-error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="mon-empty">Аниқланган нуқсонлар топилмади</div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="mon-table-wrap">
            <table className="mon-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Таяч / Линия</th>
                  <th>Филиал</th>
                  <th>Муаммолар</th>
                  <th>Статус</th>
                  <th>Муддат</th>
                  <th>Яратилди</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inc) => {
                  const overdue = isOverdue(inc.due_at, inc.status);
                  return (
                    <tr key={inc.id} onClick={() => navigate(`/monitoring/${inc.id}`)}>
                      <td style={{ color: "#8b9ab5", fontSize: 12 }}>{inc.id}</td>
                      <td>
                        <div className="mon-table__pole">
                          {inc.is_emergency && <span className="mon-emergency-dot" title="Авариявий">🚨</span>}
                          {inc.pole?.name || "—"}
                        </div>
                        {inc.pole?.network?.name && (
                          <div className="mon-table__network">{inc.pole.network.name}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: "#8b9ab5" }}>
                        {inc.branch?.name || "—"}
                      </td>
                      <td>
                        {Array.isArray(inc.no_keys) && inc.no_keys.length > 0 && (
                          <span className="mon-table__no-count">⚠ {inc.no_keys.length}</span>
                        )}
                      </td>
                      <td>
                        <span className={`mon-badge mon-badge--${STATUS_BADGE[inc.status] || "master"}`}>
                          {STATUS_LABELS[inc.status] || inc.status}
                        </span>
                        {inc.is_emergency && (
                          <span className="mon-badge mon-badge--emergency">Авариявий</span>
                        )}
                      </td>
                      <td>
                        {inc.due_at ? (
                          <span className={overdue ? "mon-overdue" : ""}>
                            {overdue && "⏰ "}
                            {formatDate(inc.due_at)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: "#8b9ab5" }}>
                        {formatDate(inc.date_created)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mon-pagination">
              <button
                className="mon-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Олдинги
              </button>
              <span className="mon-pagination__info">
                {page} / {totalPages} ({total} та)
              </span>
              <button
                className="mon-pagination__btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Кейингиси →
              </button>
            </div>
          )}
        </>
      )}
    </MonitoringLayout>
  );
}
